import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import {
  // oxlint-disable-next-line no-restricted-imports
  useMedplumProfile,
} from '@medplum/react-hooks'
import { featureFlags } from '@seen/core/featureFlags'
import { Button, cn, useIsLg } from '@seen/ui'
import { IconAlertCircle, IconHome, IconRefresh } from '@tabler/icons-react'
import { type JSX, type ReactNode, Suspense, useEffect, useRef } from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { Outlet, useLocation, useNavigate } from 'react-router'
import {
  AppNavigationMenu,
  getFirstVisibleNavPath,
} from '#src/components/app/AppNavigationMenu.tsx'
import { ClinicianActivityTracker } from '#src/components/app/ClinicianActivityTracker.tsx'
import { CommandMenu } from '#src/components/app/command-menu/CommandMenu.tsx'
import { ConfirmWithAnnotationDialog } from '#src/components/app/ConfirmWithAnnotationDialog.tsx'
import { DeleteConfirmationDialog } from '#src/components/app/DeleteConfirmationDialog.tsx'
import { DisenrollDialog } from '#src/components/app/DisenrollDialog.tsx'
import { FeedbackDialog } from '#src/components/app/FeedbackDialog.tsx'
import { FloatingAssistantButton } from '#src/components/app/FloatingAssistantButton.tsx'
import { Header } from '#src/components/app/header/Header.tsx'
import { TaskComposer } from '#src/components/app/TaskComposer.tsx'
import { Toaster } from '#src/components/app/Toaster.tsx'
import { UpsertResourceFormDialog } from '#src/components/app/UpsertResourceFormDialog.tsx'
import { ConfirmationDialog } from '#src/components/ConfirmationDialog.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ResizableHandle } from '#src/components/core/resizable/ResizableHandle.tsx'
import {
  type ImperativeResizablePanelHandle,
  ResizablePanel,
} from '#src/components/core/resizable/ResizablePanel.tsx'
import { ResizablePanelGroup } from '#src/components/core/resizable/ResizablePanelGroup.tsx'
import { GlobalFileUploadArea } from '#src/components/file-upload/GlobalFileUploadArea.tsx'
import { LabSubmissionWatcher } from '#src/components/order/LabSubmissionWatcher.tsx'
import { recordFacesheetImprovementsFlag } from '#src/components/patient/detail-content/facesheet/facesheetObservability.ts'
import { CheckInDialog } from '#src/components/patient/ops/CheckInDialog.tsx'
import { CheckOutDialog } from '#src/components/patient/ops/CheckOutDialog.tsx'
import { SignInPageComponent } from '#src/components/SignInPageComponent.tsx'
import { TimelineNoteComposerDialog } from '#src/components/TimelineNoteComposerDialog.tsx'
import { GoToHotkeys, Hotkeys } from '#src/constants/hotkeys.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useAIAssistantPatientSync } from '#src/hooks/useAIAssistantPatientSync.ts'
import { useAIAssistantStore } from '#src/hooks/useAIAssistantStore.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useDarkMode } from '#src/hooks/useDarkMode.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useEventListener } from '#src/hooks/useEventListener.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useInMemoryStore } from '#src/hooks/useInMemoryStore.ts'
import { useNavStore } from '#src/hooks/useNavStore.ts'
import { useVersionCheck } from '#src/UseVersionCheck.tsx'
import { toast } from '#src/utils/toast.ts'

declare global {
  interface Window {
    __APP_LOADED_AT__: Date
    __MENTION_NODE_OPEN_INITIAL_STATE_BY_ID__: Record<string, true>
  }
}

if (typeof window !== 'undefined') {
  window.__APP_LOADED_AT__ = new Date()
  window.__MENTION_NODE_OPEN_INITIAL_STATE_BY_ID__ = {}
}

export function App({
  isStaging,
  medplumProjectId,
  medplumGoogleClientId,
  children,
  disableEmailAuth = true,
  appVersion,
  appCommitSha,
  assistantPanel,
  accountMenuItems,
}: {
  isStaging?: boolean
  medplumProjectId?: string
  medplumGoogleClientId?: string
  children?: ReactNode
  disableEmailAuth?: boolean
  appVersion?: string
  appCommitSha?: string
  assistantPanel?: ReactNode
  accountMenuItems?: ReactNode
}): JSX.Element | null {
  const profile = useMedplumProfile()

  useEventListener('offline', () => {
    toast.error('No connection to server', {
      id: 'offline',
      duration: 0,
    })
  })
  useEventListener('online', () => {
    toast.dismiss('offline')
    toast.success('Connection to server restored', {
      id: 'online',
      duration: 2500,
    })
  })

  useVersionCheck({
    appVersion,
    appCommitSha,
  })

  const showSignedOutApp = !profile && medplumProjectId && medplumGoogleClientId

  return (
    <>
      {!showSignedOutApp && (
        <SignedInApp
          isStaging={isStaging}
          assistantPanel={assistantPanel}
          accountMenuItems={accountMenuItems}
        >
          {children}
        </SignedInApp>
      )}
      {showSignedOutApp && (
        <SignedOutApp
          medplumProjectId={medplumProjectId}
          medplumGoogleClientId={medplumGoogleClientId}
          disableEmailAuth={disableEmailAuth}
        />
      )}
      <Toaster
        closeButton
        duration={10000}
        // https://github.com/radix-ui/primitives/issues/2690#issuecomment-2009617202
        // print:hidden — toasts (e.g. "Note signed") must never render in print.
        className="pointer-events-auto print:hidden"
      />
    </>
  )
}

function SignedOutApp({
  medplumProjectId,
  medplumGoogleClientId,
  disableEmailAuth = true,
}: {
  medplumProjectId: string
  medplumGoogleClientId: string
  disableEmailAuth?: boolean
}): JSX.Element {
  useEffect(() => {
    datadogRum.clearUser()
    datadogLogs.clearUser()
  }, [])
  return (
    <SignInPageComponent
      medplumProjectId={medplumProjectId}
      medplumGoogleClientId={medplumGoogleClientId}
      disableEmailAuth={disableEmailAuth}
    />
  )
}

function SignedInApp({
  isStaging,
  children,
  assistantPanel,
  accountMenuItems,
}: {
  isStaging?: boolean
  children?: ReactNode
  assistantPanel?: ReactNode
  accountMenuItems?: ReactNode
}): JSX.Element | null {
  const currentPractitioner = useCurrentPractitioner()
  const practitionerPerms = useCurrentPractitionerPerms()
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  const { aiEnabled } = useSeenApi()
  const location = useLocation()
  const navigate = useNavigate()

  const setNavOpen = useNavStore((state) => state.setOpen)

  const pinned = useNavStore((state) => state.pinned)
  const setPinned = useNavStore((state) => state.setPinned)

  const isLg = useIsLg()
  useEffect(() => {
    // We need to use useEffect since setPinned is a store and not local state.
    setPinned(isLg)
  }, [isLg, setPinned])

  useEffect(() => {
    datadogRum.setUser({
      id: currentPractitioner.id,
      name: currentPractitioner.name,
      email: currentPractitioner.primaryEmail,
      idtRoles: currentPractitioner.idtRoles,
      // All role codes: idtRoles leaves out NP, MD, MA, LVN, CNA and the
      // non-clinical roles.
      roles: currentPractitioner.roles,
    })
    datadogLogs.setUser({
      id: currentPractitioner.id,
      name: currentPractitioner.name,
      email: currentPractitioner.primaryEmail,
    })
  }, [currentPractitioner])

  // Runs on every route change: the router starts the new RUM view before
  // this effect, and each view needs the evaluation again.
  useEffect(() => {
    recordFacesheetImprovementsFlag(facesheetImprovements)
  }, [facesheetImprovements, location.pathname])

  useEffect(() => {
    if (location.pathname !== '/') {
      return
    }

    const firstVisibleNavPath = getFirstVisibleNavPath(practitionerPerms)
    if (!firstVisibleNavPath || firstVisibleNavPath === '/') {
      return
    }

    void navigate(firstVisibleNavPath, { replace: true })
  }, [location.pathname, navigate, practitionerPerms])

  const toggleAssistant = useAIAssistantStore((state) => state.toggleOpen)
  const assistantOpen = useAIAssistantStore((state) => state.open)
  const isAssistantVisible =
    aiEnabled &&
    !!assistantPanel &&
    !practitionerPerms.isAuditor &&
    assistantOpen
  const mainPanelRef = useRef<ImperativeResizablePanelHandle>(null)

  useEffect(() => {
    const panel = mainPanelRef.current
    if (!panel) {
      return
    }
    panel.resize(isAssistantVisible ? 75 : 100)
  }, [isAssistantVisible])

  useGoToHotkeys()
  useBackHotkey()
  useAIAssistantPatientSync()
  useDarkMode()

  useHotkeys(Hotkeys.AIAssistant, toggleAssistant, {
    preventDefault: true,
    enabled: aiEnabled && !!assistantPanel && !practitionerPerms.isAuditor,
  })

  return (
    <>
      <div
        className={cn(
          'isolate flex size-full flex-col bg-frame text-frame-foreground print:block'
        )}
      >
        {currentPractitioner && (
          <Header isStaging={isStaging} accountMenuItems={accountMenuItems} />
        )}
        <div
          className={cn(
            'flex-1 lg:pr-frame lg:pb-frame',
            'min-h-0', // Prevent content from overflowing the page vertically.
            'flex print:block' // There are issues with printing and flexbox, so we avoid it when possible. All the children except one are hidden when printing, so we don't need flexbox.
          )}
        >
          {!pinned && (
            <div
              className="hidden pl-frame lg:block"
              onMouseEnter={() => setNavOpen(true)}
            />
          )}
          {pinned && <AppNavigationMenu className="print:hidden" />}
          <ResizablePanelGroup direction="horizontal">
            <ResizablePanel
              ref={mainPanelRef}
              id="app-main"
              order={1}
              defaultSize={100}
              minSize={50}
            >
              <div
                className={cn(
                  'relative size-full bg-background text-foreground',
                  'border-t lg:border print:border-none',
                  'lg:overflow-hidden',
                  isAssistantVisible
                    ? 'lg:rounded-l-md lg:border-r-0'
                    : 'lg:rounded-md',
                  pinned && 'overflow-hidden rounded-l-md border-l'
                )}
              >
                <ErrorBoundary
                  fallbackRender={({
                    error,
                    resetErrorBoundary,
                  }: FallbackProps) => (
                    <ErrorFallback
                      error={error}
                      resetError={resetErrorBoundary}
                    />
                  )}
                >
                  <Suspense fallback={<Loader />}>
                    <Outlet />
                  </Suspense>
                </ErrorBoundary>
                <FloatingAssistantButton />
              </div>
            </ResizablePanel>
            {isAssistantVisible && assistantPanel && (
              <>
                {/* print:hidden — the docked Orbit AI panel must not appear in
                    printed output (e.g. a printed visit note). The floating
                    assistant button is the panel-closed counterpart and is
                    print-hidden in its own component. */}
                <ResizableHandle className="print:hidden" />
                <ResizablePanel
                  id="ai-assistant"
                  order={2}
                  defaultSize={25}
                  minSize={15}
                  maxSize={50}
                  className="print:hidden"
                >
                  <ErrorBoundary
                    fallbackRender={({
                      error,
                      resetErrorBoundary,
                    }: FallbackProps) => (
                      <ErrorFallback
                        error={error}
                        resetError={resetErrorBoundary}
                      />
                    )}
                  >
                    <Suspense fallback={null}>{assistantPanel}</Suspense>
                  </ErrorBoundary>
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
          {children}
        </div>
      </div>
      {currentPractitioner && <CommandMenu />}
      {currentPractitioner && <FeedbackDialog />}
      {currentPractitioner && <TaskComposer />}
      {currentPractitioner && <TimelineNoteComposerDialog />}
      {currentPractitioner && <LabSubmissionWatcher />}
      <ConfirmationDialog />
      <DeleteConfirmationDialog />
      <ConfirmWithAnnotationDialog />
      <DisenrollDialog />
      <CheckInDialog />
      <CheckOutDialog />
      <UpsertResourceFormDialog />
      <GlobalFileUploadArea />
      {currentPractitioner && <ClinicianActivityTracker />}
    </>
  )
}

function ErrorFallback({
  error,
  resetError,
}: {
  error: unknown
  resetError: () => void
}): JSX.Element {
  // Output to the console so that console integrations can capture and report to respective systems
  console.error(error)

  const location = useLocation()
  const url = `${location.pathname}${location.search}${location.hash}`
  const urlChanged = useDidChange(url)
  if (urlChanged) {
    resetError()
  }

  return (
    <div className="flex h-full w-full items-center justify-center">
      <Card className="flex h-40 w-2/3 flex-col items-center justify-center">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <IconAlertCircle className="size-6" /> Something went wrong
        </h2>
        <p className="text-sm text-muted-foreground">
          The tech team has been alerted and is investigating.
        </p>
        <div className="mt-4 flex gap-3">
          <Button variant="outline" onClick={() => window.location.reload()}>
            <IconRefresh className="mr-2 size-4" />
            Try Again
          </Button>
          <Button
            onClick={() => {
              window.location.href = '/'
            }}
          >
            <IconHome className="mr-2 size-4" />
            Go Home
          </Button>
        </div>
      </Card>
    </div>
  )
}

function useGoToHotkeys(): void {
  const timeoutIdRef = useRef<number | null>(null)

  useHotkeys(Hotkeys.Go, () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
    }
    timeoutIdRef.current = window.setTimeout(() => {
      timeoutIdRef.current = null
    }, 1000)
  })

  const navigate = useNavigate()

  const goTo = (path: string): void => {
    if (timeoutIdRef.current) {
      void navigate(path)
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }
  }

  // Clean up timeout if component unmounts.
  useEffect(
    () => () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
      }
    },
    []
  )

  useHotkeys(GoToHotkeys.Inbox, () => goTo('/'))
  useHotkeys(GoToHotkeys.Calendar, () => goTo('/calendar'))
}

function useBackHotkey(): void {
  const navigate = useNavigate()
  const isPlacingDeskNode = useInMemoryStore((state) => state.isPlacingDeskNode)

  useEffect(() => {
    const ac = new AbortController()
    const maybeGoBack = (e: KeyboardEvent): void => {
      if (e.key === Hotkeys.Escape.toString()) {
        if (isPlacingDeskNode) {
          return
        }
        const openElement = document.querySelector(
          '[data-state="open"], [data-menu-dropdown="true"], .lb-tiptap-floating, dialog[open]' // data-state="open" is for any open radix-ui component, data-menu-dropdown="true" is for Mantine dropdown menus, .lb-tiptap-floating is for Liveblocks floating elements, dialog[open] is for native dialog elements like ImageZoom.
        )
        if (openElement) {
          return
        }
        void navigate(-1)
      }
    }

    document.addEventListener('keydown', maybeGoBack, {
      capture: true,
      signal: ac.signal,
    })
    return () => {
      ac.abort()
    }
  }, [navigate, isPlacingDeskNode])
}
